import { useState, useEffect } from "react";

import {
    Autocomplete,
    Button,
    Grid,
    TextField,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";

import { request } from "@/Api";
import { selectPermissions } from "@/auth/authSlice";
import {
    PERMISSION_FROM_PROJECT_STATUS,
    PERMISSION_EDIT_PROJECTS,
} from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import ConfirmationDialog from "@/common/ConfirmationDialog";
import { FEATURE_PROJECT_STATUS_FLOW } from "@/constants";
import * as projectConstants from "@/project/constants";
import WrapDialog from "@/project_common/WrapDialog";

export default function ProjectFlow({ onStatusUpdate, project, mode }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const isProjectMode = mode == "project";

    const permissions = useSelector(selectPermissions);
    const { hasPermission, hasFeature } = useEntitlements();

    const statusPermission = hasPermission(
        `${PERMISSION_FROM_PROJECT_STATUS}${project.status.name}`,
    );
    const projectStatusFlowPermission = hasFeature(FEATURE_PROJECT_STATUS_FLOW);
    const canUpdate = hasPermission(PERMISSION_EDIT_PROJECTS);

    const [wrapDialogOpen, setWrapDialogOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [inProductionDenialOpen, setInProductionDenialOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [statuses, setStatuses] = useState({});

    const getStatuses = () => {
        request.get("statuses").then((response) => {
            setStatuses(
                response.data.reduce(
                    (obj, status) => ((obj[status.name] = status), obj),
                    {},
                ),
            );
        });
    };

    const handleStatusChange = (status) => {
        if (!status) {
            throw "Missing status, please contact super admin";
        }

        if (!permissions.includes(`to_project_status:${status.name}`)) {
            setErrors({
                status_id: `"${status.name}" must be set by an authorized user.`,
            });
            return;
        }

        if (
            status.name == projectConstants.PROJECT_STATUS_IN_PRODUCTION &&
            projectStatusFlowPermission &&
            !project.legacy_bid
        ) {
            if (project.confirmed_bid && project.confirmed_bid.id) {
                onStatusUpdate(status.id);
            } else {
                setInProductionDenialOpen(true);
            }
        } else if (
            status.name == projectConstants.PROJECT_STATUS_WRAP_IN_REVIEW
        ) {
            setWrapDialogOpen(true);
        } else if (
            status.name == projectConstants.PROJECT_STATUS_CANCEL_IN_REVIEW
        ) {
            setCancelDialogOpen(true);
        } else {
            onStatusUpdate(status.id);
        }
    };

    const onWrapDialogClose = (agreed) => {
        if (agreed) {
            onStatusUpdate(
                statuses[projectConstants.PROJECT_STATUS_WRAP_IN_REVIEW].id,
            );
        }
        setWrapDialogOpen(false);
    };

    const onCancelConfirm = () => {
        setCancelDialogOpen(false);
        onStatusUpdate(
            statuses[projectConstants.PROJECT_STATUS_CANCEL_IN_REVIEW].id,
        );
    };

    const getActionButtons = () => {
        const buttons = [];
        const cancelPermission = permissions.includes(
            `to_project_status:${projectConstants.PROJECT_STATUS_CANCEL_IN_REVIEW}`,
        );
        const cancelButton = (
            <Button
                variant="contained"
                color="error"
                onClick={() =>
                    handleStatusChange(
                        statuses[
                            projectConstants.PROJECT_STATUS_CANCEL_IN_REVIEW
                        ],
                    )
                }
            >
                Cancel Project
            </Button>
        );

        switch (project.status.name) {
            case projectConstants.PROJECT_STATUS_ON_RADAR:
            case projectConstants.PROJECT_STATUS_PITCHING:
            case projectConstants.PROJECT_STATUS_BRIEFED:
            case projectConstants.PROJECT_STATUS_ON_HOLD:
            case projectConstants.PROJECT_STATUS_ONGOING:
                if (cancelPermission && isProjectMode) {
                    buttons.push(cancelButton);
                }
                break;
            case projectConstants.PROJECT_STATUS_IN_PRODUCTION:
                if (cancelPermission && isProjectMode) {
                    buttons.push(cancelButton);
                }
                buttons.push(
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                            handleStatusChange(
                                statuses[
                                    projectConstants
                                        .PROJECT_STATUS_WRAP_IN_REVIEW
                                ],
                            )
                        }
                    >
                        Request Wrap
                    </Button>,
                );
                break;
            case projectConstants.PROJECT_STATUS_CANCEL_IN_REVIEW:
                if (cancelPermission) {
                    buttons.push(
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() =>
                                handleStatusChange(
                                    statuses[
                                        projectConstants
                                            .PROJECT_STATUS_CANCELLED
                                    ],
                                )
                            }
                            disabled={!statusPermission}
                        >
                            Cancel Project
                        </Button>,
                    );
                }
                break;
            case projectConstants.PROJECT_STATUS_WRAP_IN_REVIEW:
                if (statusPermission) {
                    buttons.push(
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() =>
                                handleStatusChange(
                                    statuses[
                                        projectConstants
                                            .PROJECT_STATUS_IN_PRODUCTION
                                    ],
                                )
                            }
                        >
                            Reject
                        </Button>,
                    );
                    buttons.push(
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                                handleStatusChange(
                                    statuses[
                                        projectConstants.PROJECT_STATUS_WRAPPED
                                    ],
                                )
                            }
                        >
                            Approve
                        </Button>,
                    );
                } else {
                    buttons.push(
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {}}
                            disabled
                        >
                            Request Wrap
                        </Button>,
                    );
                }
                break;
        }
        return buttons;
    };

    useEffect(() => {
        getStatuses();
    }, []);

    return (
        <>
            <Grid
                container
                justifyContent="space-between"
                alignItems={isMobile ? "stretch" : "flex-start"}
                flexWrap={isMobile ? "wrap" : "nowrap"}
                flexDirection={isMobile ? "column" : "row"}
            >
                {isProjectMode && (
                    <Grid item flexGrow={isMobile ? 1 : 0}>
                        <Autocomplete
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            sx={{
                                marginBottom: "12px",
                                width: isMobile ? "100%" : 300,
                            }}
                            disabled={!statusPermission && canUpdate}
                            readOnly={!canUpdate}
                            value={project.status}
                            disablePortal
                            disableClearable
                            options={Object.values(statuses)}
                            getOptionLabel={(status) => status?.name}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={"status_id" in errors}
                                    helperText={errors["status_id"]}
                                />
                            )}
                            onChange={(event, status) =>
                                handleStatusChange(status)
                            }
                            blurOnSelect
                            fullWidth={isMobile}
                        />
                    </Grid>
                )}
                {projectStatusFlowPermission && (
                    <Grid item>
                        <Grid container spacing={2}>
                            {getActionButtons().map((button, i) => (
                                <Grid item key={i}>
                                    {button}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                )}
            </Grid>

            <ConfirmationDialog
                open={inProductionDenialOpen}
                closeDialog={() => setInProductionDenialOpen(false)}
                title="Hang on..."
                callback={() => setInProductionDenialOpen(false)}
                hideOk
            >
                This project&apos;s status cannot be changed to{" "}
                <strong>In Production</strong> without a bid that has been
                approved by the EP.
            </ConfirmationDialog>

            <ConfirmationDialog
                open={cancelDialogOpen}
                closeDialog={() => setCancelDialogOpen(false)}
                title="Are you sure?"
                callback={onCancelConfirm}
            >
                This will notify finance who will review and finalize closing
                this project.
            </ConfirmationDialog>

            <WrapDialog open={wrapDialogOpen} handleClose={onWrapDialogClose} />
        </>
    );
}
